<!-- eslint-disable-next-line vue/no-deprecated-slot-attribute -->
<template>
  <div class="right-panel">
    <div class="action-buttons">
        <q2-btn
            v-if="!isMarkedForDeletion && authStore.isAdmin"
            intent="workflow-destroy"
            class="icon-only-btn"
            @click="() => deleteBuildClick()"
        >
            <q2-icon type="trash" />
        </q2-btn>

        <q2-btn
            v-if="authStore.isAdmin && isMarkedForDeletion"
            intent="workflow-secondary"
            @click="() => undeleteBuildClick()"
        >
            Restore
            <q2-icon type="refresh" />
        </q2-btn>
        <div class="split-button-container">
            <q2-btn
                intent="workflow-primary"
                class="split-button-main"
                size="medium"
                @click="downloadApp"
            >
                {{ downloadButtonText }}
            </q2-btn>
            <q2-dropdown
                type="primary"
                icon="chevron-down"
                popover-alignment="right"
                class="split-button-dropdown"
            >
                <q2-dropdown-item
                    value="share"
                    removable="false"
                    @click="generatePublicLink"
                >
                    <div class="dropdown-item-content">
                        <div class="dropdown-item-title">Generate Public Install Link</div>
                        <div class="dropdown-item-description">
                            {{ build?.platform === 'android' ? 'Create .apk install link valid for 7 days' : 'Create .ipa install link valid for 7 days' }}
                        </div>
                    </div>
                </q2-dropdown-item>
                <q2-dropdown-item
                    v-if="build?.platform == 'android'"
                    value="aab"
                    removable="false"
                    @click="downloadAppBundle"
                >
                    <div class="dropdown-item-content">
                        <div class="dropdown-item-title">Download App Bundle</div>
                        <div class="dropdown-item-description">Download AAB file for Play Store submission</div>
                    </div>
                </q2-dropdown-item>
                <q2-dropdown-item
                    v-if="authStore.isAdmin"
                    value="archive"
                    removable="false"
                    @click="downloadArchive"
                >
                    <div class="dropdown-item-content">
                        <div class="dropdown-item-title">Download Build Archive</div>
                        <div class="dropdown-item-description">Download complete build files</div>
                    </div>
                </q2-dropdown-item>
            </q2-dropdown>
        </div>
    </div>

    <q2-message
        v-if="showPublicLinkMessage"
        type="success"
        appearance="standard"
        description="true"
        class="public-link-message"
    >
      A public link has been generated and copied to your clipboard!
    </q2-message>

    <!-- Shareable URLs Section -->
    <q2-section
        v-if="shareableUrls.length > 0"
        :label="`Public Shareable Links (${shareableUrls.length})`"
        collapsible
        class="shareable-urls-section"
    >
      <div class="shareable-table-wrapper">
        <q2-data-table
            class="setting-section-table"
            :headers.prop="shareableUrlsHeaders"
            :rows.prop="shareableUrlsRows"
            empty-icon="inbox"
            empty-message="No public links"
            striped="true"
        >
          <template v-for="row in shareableUrlsRows" :key="row.id">
            <div :slot="`row-${row.id}-cell-link`">
              <q2-btn
                intent="workflow-primary"
                @click="copyShareableUrl(row.cells.link, row.cells.shortKey)"
                :title="row.cells.link"
                :disabled="copiedShareableUrlId === row.cells.shortKey"
              >
                <q2-icon v-if="copiedShareableUrlId !== row.cells.shortKey" type="copy" />
                <q2-icon v-if="copiedShareableUrlId === row.cells.shortKey" type="todo" />
                {{ copiedShareableUrlId === row.cells.shortKey ? 'Copied' : row.cells.shortKey }}
              </q2-btn>
            </div>
            <div :slot="`row-${row.id}-cell-expires`">
              <div>{{ row.cells.expires }}</div>
              <q2-relative-time
                :date="row.cells.expiresAt"
                message-format="short"
                class="relative-time"
                sync="true"
              />
            </div>
            <div :slot="`row-${row.id}-cell-actions`">
              <q2-btn
                  intent="workflow-destroy"
                  class="icon-only-btn small-btn"
                  title="Revoke link"
                  @click="deleteShareableUrl(row.cells.shortKey)"
              >
                <q2-icon type="trash" />
              </q2-btn>
            </div>
          </template>
        </q2-data-table>
      </div>
    </q2-section>

    <!-- Build Setting Section -->

    <div class="settings-section settings">
      <q2-data-table
        class="setting-section-table"
        v-if="buildSettingRows"
        :headers.prop="buildSettingHeaders"
        :rows.prop="buildSettingRows"
        empty-icon="inbox"
        empty-message="Nothing to display"
        striped="true"
      >

        <div :slot="linkSlotID">
          <a :href="applicationURL" target="_blank">{{ applicationURL }}</a>
        </div>
      </q2-data-table>
    </div>



    <!-- Android Settings Section -->
     <div class="settings-section">
      <q2-data-table
        class="setting-section-table"
        v-if="androidSettingsRows && build.platform == 'android'"
        :headers.prop="osSettingsHeaders"
        :rows.prop="androidSettingsRows"
        empty-icon="inbox"
        empty-message="Nothing to display"
        striped="true"
      >
      </q2-data-table>

      <q2-data-table
        class="setting-section-table"
        v-if="iosSettingsRows && build.platform == 'ios'"
        :headers="osSettingsHeaders"
        :rows="iosSettingsRows || []"
        empty-icon="inbox"
        striped="true"
      >
      </q2-data-table>
    </div>

    <!-- Features Section -->
    <div class="settings-section">
      <q2-data-table
        class="setting-section-table"
        v-if="featuresRows"
        :headers.prop="featuresHeaders"
        :rows.prop="featuresRows"
        empty-icon="inbox"
        empty-message="Nothing to display"
        striped="true"
      >
      </q2-data-table>
    </div>
    <!-- Dev/Beta Features Section -->
    <div class="settings-section">
      <q2-data-table
        class="setting-section-table"
        v-if="devBetaRows"
        :headers.prop="devBetaHeaders"
        :rows.prop="devBetaRows"
        empty-icon="inbox"
        empty-message="Nothing to display"
        striped="true"
      >
      </q2-data-table>
    </div>

    <!-- Installed Modules Section -->
    <div class="settings-section">
      <q2-data-table
        class="setting-section-table"
        v-if="installedModulesRows"
        :headers.prop="installedModulesHeaders"
        :rows.prop="installedModulesRows"
        empty-icon="inbox"
        empty-message="Nothing to display"
        striped="true"
      >
      </q2-data-table>
    </div>

    <!-- App Link Settings Section -->
    <div class="settings-section">
      <q2-data-table
        class="setting-section-table"
        v-if="appLinkRows && build.platform == 'android'"
        :headers.prop="appLinkHeaders"
        :rows.prop="appLinkRows"
        empty-icon="inbox"
        empty-message="Nothing to display"
        striped="true"
      >
        <div :slot="wellKnownSlotID">
          <a :href="wellKnownURL" target="_blank">assetlinks.json</a>
        </div>
      </q2-data-table>
    </div>

    <div class="settings-section">
      <q2-data-table
        class="setting-section-table"
        v-if="universalLinkRows && build.platform == 'ios'"
        :headers.prop="universalLinkHeaders"
        :rows.prop="universalLinkRows"
        empty-icon="inbox"
        empty-message="Nothing to display"
        striped="true"
      >

        <div :slot="wellKnownSlotID">
        <a :href="wellKnownURL" target="_blank">apple-app-site-association</a>
        </div>
      </q2-data-table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import type { FiBuild, ModuleInfo } from "../types/build";
import { useAuthStore } from "../stores/authStore";
import { logger } from "../utils/logger";
import { generatePublicDownloadLink, listShareableUrls, revokeShareableUrl } from "../services/api";

const authStore = useAuthStore();

const complianceLogoLabels: Record<string, string> = {
  "1": "FDIC",
  "2": "NCUA",
};
const complianceLogoLabel = (val?: string) => complianceLogoLabels[val ?? ""] ?? "N/A";
const linkGenerated = ref(false);
const generatingLink = ref(false);
const showPublicLinkMessage = ref(false);

// Shareable URLs
const shareableUrls = ref([]);
const loadingShareableUrls = ref(false);
const copiedShareableUrlId = ref<string | null>(null);

// Device detection
const isMobile = computed(() => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
});

const isIOS = computed(() => {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
});

const isAndroid = computed(() => {
  return /Android/i.test(navigator.userAgent);
});

const downloadButtonText = computed(() => {
  return isMobile.value ? "Install App" : "Download App";
});

const shareableUrlsHeaders = ref([
  { title: "Link", key: "link" },
  { title: "Expires", key: "expires" },
  { title: "Views", key: "views" },
  { title: "", key: "actions" },
]);

const shareableUrlsRows = computed(() => {
  if (!shareableUrls.value || shareableUrls.value.length === 0) return [];

  const baseUrl = process.env.VUE_APP_BASE_URL || window.location.origin;
  const build = resolvedBuild.value;
  const fiNumber = build?.fiNumber || props.buildId.split("-")[0];

  return shareableUrls.value.map((url, index) => ({
    id: `share-${index}`,
    cells: {
      link: `${baseUrl}/s/${fiNumber}/${url.shortKey}`,
      expires: formatTimestamp(url.expiresAt),
      expiresAt: url.expiresAt, // ISO string for q2-relative-time
      views: url.accessCount || 0,
      actions: "",
      shortKey: url.shortKey, // For delete action
    },
  }));
});

interface Props {
  buildId: string;
  build: object | null;
}

const props = defineProps<Props>();

const buildSettingHeaders = ref([
  { title: "Build Setting", key: "setting" },
  { title: "", key: "value" },
]);

const osSettingsHeaders = ref([
  { title: "OS Settings", key: "setting" },
  { title: "", key: "value" },
]);

const featuresHeaders = ref([
  { title: "Features", key: "feature" },
  { title: "Status", key: "value" },
]);

const devBetaHeaders = ref([
  { title: "Dev/Beta Features", key: "feature" },
  { title: "Status", key: "status" },
]);

const installedModulesHeaders = ref([
  { title: "Installed Modules", key: "module" },
  { title: "Version", key: "version" },
  { title: "Route", key: "route" },
]);

const appLinkHeaders = ref([
  { title: "App Link Settings", key: "setting" },
  { title: "", key: "value" },
]);


const universalLinkHeaders = ref([
  { title: "Settings", key: "setting" },
  { title: "", key: "value" },
]);


const resolvedBuild = computed<FiBuild | null>(() => {
  if (!props.buildId || !props.build) return null;

  return props.build;
});

const applicationURL = computed(() => {
  const b: Partial<FiBuild> = resolvedBuild.value || {};

  return b.buildSettings.targetURLBase + "/" + b.buildSettings.targetPageName;
});

const linkSlotID = computed(() => {
  const baseId = props.buildId || "unknown";

  return "row-" + baseId + "-3-cell-value";
});

const wellKnownURL = computed(() => {
  const b: Partial<FiBuild> = resolvedBuild.value || {};

  return "/v1/build/" + b.buildId + "/download?type=well-known";
});

const wellKnownSlotID = computed(() => {
  const baseId = props.buildId || "unknown";

  return "row-" + baseId + "-link-1-cell-value";
});

function formatTimestamp(isoString) {
  if (!isoString) return "N/A";
  const date = new Date(isoString);
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

const buildSettingRows = computed(() => {
  const b: Partial<FiBuild> = resolvedBuild.value || {};
  const baseId = props.buildId || "unknown";
  return [
    {
      id: `${baseId}-0`,
      cells: {
        setting: "App Name",
        value: b.buildSettings.applicationName || "N/A",
      },
    },
    {
      id: `${baseId}-1`,
      cells: {
        setting: "Application Version",
        value: b.buildSettings.containerVersion || "N/A",
      },
    },
    {
      id: `${baseId}-2`,
      cells: {
        setting: "Build Time",
        value: formatTimestamp(b.buildSettings.lastBuild) || "N/A",
      },
    },
    {
      id: `${baseId}-3`,
      cells: {
        setting: "Application URL",
        value: b.buildSettings.targetURLBase + "/" + b.buildSettings.targetPageName || "N/A",
      },
    },
    {
      id: `${baseId}-4`,
      cells: { setting: "Build Branch", value: b.buildSettings.codeBranch || "N/A" },
    },
    {
      id: `${baseId}-5`,
      cells: {
        setting: "Build Reason",
        value: b.buildReason || "N/A",
      },
    },
  ];
});

const androidSettingsRows = computed(() => {
  const b: Partial<FiBuild> = resolvedBuild.value || {};
  const baseId = props.buildId || "unknown";
  /* eslint-disable */
  const tickIfTrue = (val?: boolean | string | any[]) => {
    if (val === true) return "✓"; // strictly true
    if (typeof val === "string") return val.length > 0 ? "✓" : "✕";
    if (Array.isArray(val)) return val.length > 0 ? "✓" : "✕";
    if (val === false) return "✕";
    return "✕";
  };

  return [
    {
      id: `${baseId}-a-0`,
      cells: { setting: "Package", value: b.buildSettings.package || "N/A" },
    },
    {
      id: `${baseId}-a-1`,
      cells: {
        setting: "Contacts for UUX/Tecton",
        value: tickIfTrue(b.buildSettings.contacts),
      },
    },
    {
      id: `${baseId}-a-2`,
      cells: { setting: "Framework Version", value: b.version || "N/A" },
    }, // idk if this is right
    {
      id: `${baseId}-a-3`,
      cells: {
        setting: "Third Party App Package Name(s)",
        value: b.buildSettings.packageNameQueries || "N/A",
      },
    },
    {
      id: `${baseId}-a-4`,
      cells: {
        setting: "Google Cloud API Key",
        value: tickIfTrue(b.buildSettings.googleCloudApiKey),
      },
    },
    {
      id: `${baseId}-a-5`,
      cells: {
        setting: "Version Offset",
        value: b.buildSettings.androidVersionCodeOffset,
      },
    },
    {
      id: `${baseId}-a-6`,
      cells: {
        setting: "App Device Integrity (ADI) Registration",
        value: b.buildSettings.adiRegistration || "N/A",
      },
    },
  ];
});

const iosSettingsRows = computed(() => {
  const b: Partial<FiBuild> = resolvedBuild.value || {};

  /* eslint-disable */
  const tickIfTrue = (val?: boolean | string | any[]) => {
    if (val === true) return '✓'; // strictly true
    if (typeof val === 'string') return val.length > 0 ? '✓' : '✕';
    if (Array.isArray(val)) return val.length > 0 ? '✓' : '✕';
    if (val === false) return '✕';
    return '✕';
  };

  const baseId = props.buildId || 'unknown';
  return [
    { id: `${baseId}-ios-0`, cells: { setting: 'Application Bundle Identifier', value: b.buildSettings.bundleIdentifier || 'N/A' } },
    { id: `${baseId}-ios-1`, cells: { setting: 'Application Bundle Identifier Prefix', value: b.buildSettings.bundleIdentifierPrefix || 'N/A' } },
    { id: `${baseId}-ios-2`, cells: { setting: 'App Store Version Number', value: b.buildSettings.appStoreVersion || 'N/A' } },
    { id: `${baseId}-ios-3`, cells: { setting: 'Application Bundle Version', value: b.buildSettings.bundleVersion || 'N/A' } },
    { id: `${baseId}-ios-4`, cells: { setting: 'URL Schemes', value: b.buildSettings.urlScheme || 'N/A' } },
    { id: `${baseId}-ios-5`, cells: { setting: 'Apple Watch', value: tickIfTrue(b.buildSettings.appleWatchEnabled) } },
    { id: `${baseId}-ios-6`, cells: { setting: 'Apple Watch API URL', value: b.buildSettings.preLoginBalanceURL || 'N/A' } },
    { id: `${baseId}-ios-7`, cells: { setting: 'Apple Watch Bundle Identifier', value: b.buildSettings.bundleIdentifierWatch || 'N/A' } },
    { id: `${baseId}-ios-8`, cells: { setting: 'Apple Watch Bundle Identifier Extension', value: b.buildSettings.bundleIdentifierWatchExtension || 'N/A' } }
  ];
});

const featuresRows = computed(() => {
  const b: Partial<FiBuild> = resolvedBuild.value || {};
  const baseId = props.buildId || "unknown";
  /* eslint-disable */
  const tickIfTrue = (val?: boolean | string | any[]) => {
    if (val === true) return "✓"; // strictly true
    if (typeof val === "string") return val.length > 0 ? "✓" : "✕";
    if (Array.isArray(val)) return val.length > 0 ? "✓" : "✕";
    if (val === false) return "✕";
    return "✕";
  };

  return [
    {
      id: `${baseId}-f-1`,
      cells: {
        feature: "Native Login Footer Text",
        value: tickIfTrue(b.buildSettings.loginFooter),
      },
    },
    {
      id: `${baseId}-f-2`,
      cells: { feature: "Native Menu", value: tickIfTrue(b.buildSettings.nativeMenu) },
    },
    {
      id: `${baseId}-f-3`,
      cells: {
        feature: "Native Enroll: Four Digit Pin / Touch ID",
        value: tickIfTrue(b.buildSettings.q2configTouchEnrollment),
      },
    },
    {
      id: `${baseId}-f-4`,
      cells: { feature: "Native mRDC", value: tickIfTrue(b.buildSettings.nativeMrdc) },
    },
    {
      id: `${baseId}-f-5`,
      cells: { feature: "Vertifi Camera", value: tickIfTrue(b.buildSettings.vertifi) },
    },
    {
      id: `${baseId}-f-6`,
      cells: { feature: "Push Notifications", value: tickIfTrue(b.buildSettings.push) },
    },
    {
      id: `${baseId}-f-7`,
      cells: { feature: "App Ratings", value: tickIfTrue(b.buildSettings.appRatings) },
    },
    {
      id: `${baseId}-f-8`,
      cells: {
        feature: "Compliance Logo and Text",
        value: complianceLogoLabel(b.buildSettings.complianceLogo),
      },
    },
    {
      id: `${baseId}-f-10`,
      cells: {
        feature: "(LA) Hide Overbrowser URL",
        value: tickIfTrue(b.buildSettings.hideOverBrowserUrl),
      },
    },
    {
      id: `${baseId}-f-11`,
      cells: {
        feature: "Age Signals",
        value: tickIfTrue(b.buildSettings.useAgeServiceAPI),
      },
    },
    {
      id: `${baseId}-f-12`,
      cells: {
        feature: "Passkey Support for 3rd Party IDP",
        value: tickIfTrue(b.buildSettings.passkeySupport),
      },
    },
  ];
});

const devBetaRows = computed(() => {
  const b: Partial<FiBuild> = resolvedBuild.value || {};
  const baseId = props.buildId || "unknown";
  const rows = [];
  /* eslint-disable */
  const tickIfTrue = (val?: boolean | string | any[]) => {
    if (val === true) return "✓"; // strictly true
    if (typeof val === "string") return val.length > 0 ? "✓" : "✕";
    if (Array.isArray(val)) return val.length > 0 ? "✓" : "✕";
    if (val === false) return "✕";
    return "✕";
  };

  if (b.buildSettings.enableDevMode !== undefined)
    rows.push({
      id: `${baseId}-devmode`,
      cells: {
        feature: "Dev Settings",
        status: tickIfTrue(b.buildSettings.enableDevMode),
      },
    });
  if (b.buildSettings.deviceMicEnabled !== undefined)
    rows.push({
      id: `${baseId}-mic`,
      cells: {
        feature: "Microphone permissions enabled",
        status: tickIfTrue(b.buildSettings.deviceMicEnabled),
      },
    });
  return rows;
});

const installedModulesRows = computed(() => {
  const modules: ModuleInfo[] = resolvedBuild.value?.buildSettings?.modules || [];

  const baseId = props.buildId || "unknown";
  return modules
    .filter((m) => m.enabled && !m.name?.toLowerCase().includes("internal"))
    .map((m, index) => ({
      id: `${baseId}-mod-${index}`,
      cells: {
        module: m.name || m.identifier || "Unknown Module",
        version: m.version || "N/A",
        route: m.route || m.classPath || "N/A",
      },
    }));
});

const appLinkRows = computed(() => {
  const b: Partial<FiBuild> = resolvedBuild.value || {};
  const baseId = props.buildId || "unknown";
  return [
    {
      id: `${baseId}-link-0`,
      cells: {
        setting: "Associated Domain",
        value: b.buildSettings.universalAndAppLinkAssociatedDomain || "N/A",
      },
    },
    {
      id: `${baseId}-link-1`,
      cells: { setting: "Well-Known File", value: "assetlinks.json" },
    },
  ];
});

const universalLinkRows = computed(() => {
  const b: Partial<FiBuild> = resolvedBuild.value || {};

  const baseId = props.buildId || 'unknown';
  return [
    { id: `${baseId}-link-0`, cells: { setting: 'Associated Domain', value: b.buildSettings.universalAndAppLinkAssociatedDomain || 'N/A' } },
    { id: `${baseId}-link-1`, cells: { setting: 'Well-Known File', value: 'apple-app-site-association' } }
  ];
});



/* global defineEmits */
const emit = defineEmits<{
  (e: "download-app"): void;
  (e: "download-app-bundle"): void;
  (e: "download-archive"): void;
  (e: "delete-build"): void;
  (e: "undelete-build"): void;
}>();

// The API stamps `markedForDeletion` (a timestamp) on soft delete; there is no
// `markedToDelete` field on the wire, so the old name was always undefined.
const isMarkedForDeletion = computed(() => {
  return resolvedBuild.value?.markedForDeletion != null;
});

async function downloadApp() {
  logger.debug("📦 Download App clicked");

  const build = resolvedBuild.value;
  const buildPlatform = build?.platform?.toLowerCase();

  // If on mobile iOS and build is iOS, use sideload for OTA installation
  if (isMobile.value && isIOS.value && buildPlatform === 'ios') {
    logger.debug("📱 iOS mobile device detected, triggering OTA installation");
    try {
      // Generate a shareable public link for the sideload manifest (no auth required)
      const response = await generatePublicDownloadLink(props.buildId, 'manifest');

      const fiNumber = build?.fiNumber || props.buildId.split("-")[0];
      const shortKey = response.shortKey;

      // The /v1/s endpoint is unauthenticated, so target the API host directly rather
      // than relying on the website's nginx proxy. iOS itms-services requires an absolute URL.
      const apiUrl = process.env.VUE_APP_API_URL;
      if (!apiUrl) {
        logger.error("VUE_APP_API_URL is not set; cannot build iOS manifest URL");
        alert("Configuration error: API URL is not set.");
        return;
      }
      const manifestUrl = `${apiUrl}/v1/s/${fiNumber}/${shortKey}`;

      // Create iOS OTA install URL - iOS is picky about encoding, only encode special chars
      // Don't use encodeURIComponent as it encodes : and / which breaks the URL
      const encodedUrl = manifestUrl.replace(/&/g, '%26').replace(/\?/g, '%3F').replace(/#/g, '%23');
      const installUrl = `itms-services://?action=download-manifest&url=${encodedUrl}`;

      logger.debug("📱 Manifest URL:", manifestUrl);
      logger.debug("🔗 Encoded URL:", encodedUrl);
      logger.debug("🔗 Install URL:", installUrl);

      // Trigger iOS OTA installation
      window.location.href = installUrl;
    } catch (err) {
      logger.error("❌ Failed to trigger iOS installation:", err);
      alert("Failed to initiate installation. Please try downloading instead.");
    }
    return;
  }

  // If on mobile Android and build is Android, download APK
  if (isMobile.value && isAndroid.value && buildPlatform === 'android') {
    logger.debug("📱 Android mobile device detected, downloading APK");
    emit("download-app");
    return;
  }

  // Desktop or other scenarios - use regular download
  emit("download-app");
}

function downloadAppBundle() {
  logger.debug("📦 Download App Bundle clicked");
  emit("download-app-bundle");
}

function downloadArchive() {
  logger.debug("📦 Download Archive clicked");
  emit("download-archive");
}

async function generatePublicLink() {
  // Show confirmation dialog
  const confirmed = window.confirm(
      "Are you sure you want to generate a public download link and copy it to your clipboard?\n\n" +
      "This link will be valid for 7 days and can be shared with anyone. You can remove public access at any time by deleting the link under the public links section."
  );

  if (!confirmed) {
    logger.debug("User cancelled public link generation");
    return;
  }

  generatingLink.value = true;
  try {
    const build = resolvedBuild.value;
    const downloadType = build?.platform === 'android' ? 'app' : 'app'; // Could be 'aab' for Android App Bundle

    const response = await generatePublicDownloadLink(props.buildId, downloadType);

    // Extract fiNumber and shortKey from the API response
    const fiNumber = build?.fiNumber || props.buildId.split('-')[0];
    const shortKey = response.shortKey;

    // Build the shareable URL using the distribution site's base URL
    // Use env variable if present, otherwise fall back to current host
    const baseUrl = process.env.VUE_APP_BASE_URL || window.location.origin;
    const publicUrl = `${baseUrl}/s/${fiNumber}/${shortKey}`;

    if (publicUrl) {
      // Try to copy to clipboard, with fallback for focus issues after confirm dialog
      try {
        await navigator.clipboard.writeText(publicUrl);
        logger.debug("✅ Generated and copied public download link:", publicUrl);
      } catch (clipboardErr) {
        // Fallback: use a temporary textarea if clipboard API fails
        logger.debug("Clipboard API failed, using fallback method");
        const textarea = document.createElement('textarea');
        textarea.value = publicUrl;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        try {
          document.execCommand('copy');
          logger.debug("✅ Generated and copied public download link (fallback):", publicUrl);
        } catch (fallbackErr) {
          logger.error("❌ Failed to copy with fallback method:", fallbackErr);
        }
        document.body.removeChild(textarea);
      }

      logger.debug("Link expires at:", response.expiresAt);
      linkGenerated.value = true;
      setTimeout(() => {
        linkGenerated.value = false;
      }, 2000);

      // Show success message for 5 seconds
      showPublicLinkMessage.value = true;
      setTimeout(() => {
        showPublicLinkMessage.value = false;
      }, 5000);

      // Reload shareable URLs list
      await loadShareableUrls();
    } else {
      logger.error("❌ No download link in response");
    }
  } catch (err) {
    logger.error("❌ Failed to generate public download link:", err);
  } finally {
    generatingLink.value = false;
  }
}

function deleteBuildClick() {
  emit("delete-build");
}
function undeleteBuildClick() {
  logger.debug("♻️ Undelete clicked");
  emit("undelete-build"); // You’ll handle this in the parent
}

// Load shareable URLs when component mounts
onMounted(async () => {
  await loadShareableUrls();
});

// Reload shareable URLs when buildId changes
watch(() => props.buildId, async (newBuildId) => {
  if (newBuildId) {
    await loadShareableUrls();
  }
});

async function loadShareableUrls() {
  if (!props.buildId) return;

  loadingShareableUrls.value = true;
  try {
    const response = await listShareableUrls(props.buildId);
    shareableUrls.value = response.shares || [];
    logger.debug("✅ Loaded shareable URLs:", shareableUrls.value);
  } catch (err) {
    logger.error("❌ Failed to load shareable URLs:", err);
    shareableUrls.value = [];
  } finally {
    loadingShareableUrls.value = false;
  }
}

async function deleteShareableUrl(shortKey) {
  const confirmed = window.confirm("Are you sure you want to revoke this shareable link?");
  if (!confirmed) return;

  try {
    const build = resolvedBuild.value;
    const fiNumber = build?.fiNumber || props.buildId.split("-")[0];

    await revokeShareableUrl(fiNumber, shortKey);
    logger.debug("✅ Revoked shareable URL:", shortKey);

    // Reload the list
    await loadShareableUrls();
  } catch (err) {
    logger.error("❌ Failed to revoke shareable URL:", err);
    alert("Failed to revoke shareable link");
  }
}

function copyShareableUrl(url, shortKey) {
  navigator.clipboard.writeText(url)
    .then(() => {
      logger.debug("✅ Copied shareable URL:", url);
      copiedShareableUrlId.value = shortKey;
      setTimeout(() => {
        copiedShareableUrlId.value = null;
      }, 5000);
    })
    .catch((err) => {
      logger.error("❌ Failed to copy URL:", err);
    });
}
</script>

<style scoped>
.public-link-message {
  margin-bottom: 16px;
}

.action-buttons {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  align-items: center;
}

.small-btn {
  min-width: auto !important;
  padding: 4px 8px !important;
}

.shareable-urls-section {
  margin-bottom: 16px;
  margin-left: 0;
  margin-right: 0;
  --tct-section-margin: 0 0 16px 0;
}

.shareable-table-wrapper {
  margin: -10px -15px;
  padding: 0;
}

.shareable-table-wrapper :deep(q2-data-table) {
  width: 100%;
}

.shareable-table-wrapper :deep(table) {
  width: 100%;
}

.shareable-urls-section :deep(tbody tr) {
  height: 40px;
}

.shareable-urls-section :deep(tbody td) {
  padding: 8px 12px;
  vertical-align: middle;
}

.shareable-urls-section :deep(tbody td a) {
  display: inline;
  line-height: 1.5;
}

.shareable-urls-section :deep(tbody td .icon-only-btn) {
  padding: 4px !important;
}

.shareable-urls-section :deep(.relative-time) {
  font-size: 11px;
  color: #999;
  display: block;
  margin-top: 2px;
}

.split-button-container {
  display: flex;
  gap: 0;
  align-items: stretch;
  height: 100%;
}

.split-button-main {
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
  border-right: 1px solid rgba(255, 255, 255, 0.3) !important;
  margin-right: 0 !important;
  min-height: 44px !important;
  height: 100% !important;
  display: inline-flex !important;
}

.split-button-dropdown {
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
  margin-left: 0 !important;

  /* Override Tecton CSS variables for neutral buttons */
  --tct-btn-neutral-background: #594bab !important;
  --tct-btn-neutral-background-hover: #4a3d8f !important;
  --tct-btn-neutral-color: white !important;
}

/* Target the button inside the dropdown and override transparent background */
.split-button-dropdown :deep(button) {
  background: #594bab !important;
  color: white !important;
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
  padding: 8px 12px !important;
  min-width: auto !important;
  height: 100% !important;
}

.split-button-dropdown :deep(button:hover) {
  background: #4a3d8f !important;
}

.setting-section-table {
    font-size:10pt;
}

.setting-section-table {
    --tct-data-table-header-background: #faf7fd;
    --tct-data-table-header-border-color: rgb(216.75, 216.75, 216.75);
    color: #404040;
}

.right-panel {
  --tct-btn-primary-background: #594bab;
  --tct-btn-neutral-background: #faf7fd;
  --tct-btn-primary-box-shadow: none;
  --tct-btn-neutral-box-shadow: none;
  --tct-btn-border-radius: 5px;
}

.icon-only-btn {
  min-width: auto !important;
  max-width: fit-content;
  width: auto !important;
  padding: 8px !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 768px) {
  /* Make action buttons sticky at top for easy access */
  .action-buttons {
    position: sticky;
    top: 0;
    z-index: 10;
    background: white;
    padding: 12px 0;
    margin-bottom: 20px;
    border-bottom: 2px solid #e5e7eb;
    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  }

  /* Make download button larger and more prominent on mobile */
  .split-button-main {
    min-height: 48px !important;
    font-size: 16px !important;
    font-weight: 600 !important;
  }

  .split-button-dropdown :deep(button) {
    min-height: 48px !important;
  }

  /* Show all settings sections on mobile, not just the first one */
  .settings-section {
    display: block;
    margin-bottom: 16px;
  }

  /* Add visual separation between sections */
  .settings-section + .settings-section {
    padding-top: 16px;
    border-top: 1px solid #e5e7eb;
  }
}

.dropdown-item-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 4px 0;
}

.dropdown-item-title {
  font-size: 14px;
  font-weight: 500;
  color: #404040;
}

.dropdown-item-description {
  font-size: 12px;
  color: #666;
  line-height: 1.3;
}
</style>
